import { isAnyOf } from '@reduxjs/toolkit';
import { fetchTasks } from './operatorTasksThunks';
import {
  operatorClockStarted,
  operatorClockStopped,
  operatorClockTicked,
  selectTasksLoadedHere,
} from './operatorTasksSlice';
import { TASKS_CLOCK_MS } from './operatorTasksUtils';
import { closeReceipt } from '../operatorReceiving/operatorReceivingThunks';
import { pickLine, shortPickLine } from '../operatorPicking/operatorPickingThunks';
import { reportShortAtStop, takeAtStop } from '../operatorPicking/operatorPickingRouteThunks';
import { isRouteFinished } from '../operatorPicking/operatorPickingRouteUtils';
import {
  addItem,
  addItemProduct,
  addItemSupply,
  removeItem,
  removeItemSupply,
  saveItem,
} from '../operatorPacking/operatorPackingThunks';
import { packedByWrite } from '../operatorPacking/operatorPackingUtils';
import { storePutaway } from '../operatorPutaway/operatorPutawayThunks';
import { emptiesPutawayRow } from '../operatorPutaway/operatorPutawayUtils';
import { dispatchLoad } from '../operatorDispatch/operatorDispatchThunks';

const isPackingWrite = isAnyOf(
  addItem.fulfilled,
  saveItem.fulfilled,
  addItemProduct.fulfilled,
  removeItem.fulfilled,
  addItemSupply.fulfilled,
  removeItemSupply.fulfilled
);

const isPick = isAnyOf(pickLine.fulfilled, shortPickLine.fulfilled);

const isRouteWrite = isAnyOf(takeAtStop.fulfilled, reportShortAtStop.fulfilled);

/**
 * Whether an outcome is the operator finishing a task: the order packed —
 * also by the replay of the write that packed it —, the receipt closed, the
 * order or the route left with nothing to pick, the row stored whole, a load
 * that dispatched some order. Wherever it lands, on screen or not.
 */
export const finishesTask = (action) => {
  if (closeReceipt.fulfilled.match(action)) return true;
  if (isPackingWrite(action)) return packedByWrite(action.payload);
  if (isPick(action)) return Number(action.payload?.order?.pending_units) === 0;
  if (isRouteWrite(action)) return isRouteFinished(action.payload?.route);
  if (storePutaway.fulfilled.match(action)) return emptiesPutawayRow(action.meta.arg);
  if (dispatchLoad.fulfilled.match(action)) return Number(action.payload?.dispatched) > 0;
  return false;
};

/**
 * The tray's reactions. It is asked again when a task is finished, and only
 * if it was ever loaded in this warehouse — an operator who never opened
 * Inicio here asks nothing. While Inicio sits still, the clock's tick asks it
 * too (see `operatorRefresh`).
 *
 * @param {Function} startListening
 */
export const registerOperatorTasksListeners = (startListening) => {
  startListening({
    predicate: (action, state) => finishesTask(action) && selectTasksLoadedHere(state),
    effect: (_, { dispatch }) => {
      dispatch(fetchTasks());
    },
  });

  // The local clock: it moves «Esperando hace…» on screen, and `operatorRefresh` asks the lists on its tick.
  startListening({
    actionCreator: operatorClockStarted,
    effect: async (_, { dispatch, cancelActiveListeners, fork, take }) => {
      cancelActiveListeners();
      dispatch(operatorClockTicked(Date.now()));
      const ticking = fork(async ({ delay }) => {
        for (;;) {
          await delay(TASKS_CLOCK_MS);
          dispatch(operatorClockTicked(Date.now()));
        }
      });
      await take(operatorClockStopped.match);
      ticking.cancel();
    },
  });
};
